// ── Sales tax filing → receipt for the bookkeeper (2026-10-16) ───────────
// Phase 5 of commitSalesTaxFilings, pulled out so it can be EXECUTED in a
// test against a recording Supabase (CLAUDE.md rule since the fee_share
// incident: a DB-writing cascade needs an executing test). One receipt of
// kind 'sales_tax' per filing with money paid, keyed on
// receipts.sales_tax_filing_id; a $0 filing has none and drops one it had.
// The portal PDF uploaded on THIS save is copied into the `receipts`
// bucket (Dan's preview reads only that bucket); with no new file the
// receipt keeps the copy it has.
import { supabase } from './supabase';
import { buildSalesTaxReceipt, salesTaxReturnLabel, receiptToRow, rowToReceipt } from '../utils';

function r2(n) { return Math.round((Number(n) || 0) * 100) / 100; }

// args: { entries:[{filing}], newFiles:[{file}], receipts, stateTaxRecords,
//         salesTaxCodes, who, mintId, setReceipts }
// → { ok:true, receipts:[saved] } | { ok:false, error }
export async function mirrorSalesTaxReceipts(args) {
  var entries = args.entries || [];
  var newFiles = args.newFiles || [];
  var receipts = args.receipts || [];
  var setReceipts = args.setReceipts || function() {};
  var mintId = args.mintId || function() { return crypto.randomUUID(); };
  var saved = [];
  for (var m = 0; m < entries.length; m++) {
    var fl = entries[m].filing;
    var mirror = receipts.find(function(x) { return x.salesTaxFilingId === fl.id; }) || null;
    var paid = r2(fl.amountPaid);
    if (paid <= 0) {
      if (mirror) {
        var gone = await supabase.from('receipts').delete().eq('id', mirror.id);
        if (gone.error) return { ok: false, error: 'The filing IS recorded, but its old bookkeeper copy could not be removed: ' + gone.error.message };
        setReceipts(function(prev) { return prev.filter(function(x) { return x.id !== mirror.id; }); });
        if (mirror.storagePath) { try { await supabase.storage.from('receipts').remove([mirror.storagePath]); } catch (_e) { /* best effort */ } }
      }
      continue;
    }
    var rid = mirror ? mirror.id : mintId();
    var fileMeta = {};
    var nf0 = newFiles[0] && newFiles[0].file;
    if (nf0) {
      var rext = ((nf0.name || '').split('.').pop() || 'pdf').toLowerCase().replace(/[^a-z0-9]/g, '') || 'pdf';
      var rpath = 'sales_tax/' + new Date().toISOString().slice(0, 7) + '/' + rid + '_' + mintId() + '.' + rext;
      var rup = await supabase.storage.from('receipts').upload(rpath, nf0, { contentType: nf0.type || 'application/pdf', upsert: false });
      if (rup.error) return { ok: false, error: 'The filing IS recorded, but the bookkeeper copy of ' + nf0.name + ' could not be uploaded: ' + (rup.error.message || rup.error) + ' — press Save again to retry.' };
      fileMeta = { storagePath: rpath, filename: nf0.name || '', mimeType: nf0.type || 'application/pdf' };
      if (mirror && mirror.storagePath) { try { await supabase.storage.from('receipts').remove([mirror.storagePath]); } catch (_e) { /* best effort */ } }
    }
    var stateRec = (args.stateTaxRecords || []).find(function(r) { return r.stateCode === fl.stateCode && !r.jurisdiction; }) || {};
    var rec = buildSalesTaxReceipt(fl, Object.assign({ id: rid, existing: mirror, who: args.who || '', stateName: stateRec.stateName || '', returnLabel: salesTaxReturnLabel(fl, args.salesTaxCodes || []) }, fileMeta));
    var up = await supabase.from('receipts').upsert(receiptToRow(rec), { onConflict: 'id' }).select().single();
    if (up.error) {
      if (fileMeta.storagePath) { try { await supabase.storage.from('receipts').remove([fileMeta.storagePath]); } catch (_e) { /* best effort */ } }
      var hint = /receipts_kind_check|sales_tax_filing_id/.test(String(up.error.message || '')) ? ' — run 20261016_receipts_sales_tax_kind.sql.' : ' — press Save again to retry.';
      return { ok: false, error: 'The filing IS recorded, but the bookkeeper copy could not be saved: ' + up.error.message + hint };
    }
    var savedR = rowToReceipt(up.data || receiptToRow(rec));
    saved.push(savedR);
    setReceipts(function(prev) {
      var hit = prev.some(function(x) { return x.id === savedR.id; });
      return hit ? prev.map(function(x) { return x.id === savedR.id ? savedR : x; }) : prev.concat([savedR]);
    });
  }
  return { ok: true, receipts: saved };
}

// After a filing is deleted (the DB cascades the receipt row): drop the
// bookkeeper copy from state and its file from the bucket.
export async function dropSalesTaxReceiptMirror(args) {
  var mirror = (args.receipts || []).find(function(x) { return x.salesTaxFilingId === args.filingId; });
  if (!mirror) return null;
  if (args.setReceipts) args.setReceipts(function(prev) { return prev.filter(function(x) { return x.id !== mirror.id; }); });
  if (mirror.storagePath) { try { await supabase.storage.from('receipts').remove([mirror.storagePath]); } catch (_e) { /* best effort */ } }
  return mirror;
}
